/* eslint-disable sonarjs/no-duplicate-string */
import {
  ActiveSyllabusItemTypeDto,
  DeprecatedSyllabusItemTypeDto,
  SyllabusItemTypeDto
} from '../apis/sherpath-syllabus-service/sherpath-syllabus-service.constants';
import { RecContentItemTypeDto } from '../apis/rec-gateway/rec-gateway.dtos';
import { AssignmentType } from '../apis/eols-assessment-service/eols-assessment-service.dtos';

export enum ResourceCategory {
  QUIZZES_ASSESSMENTS = 'QUIZZES_ASSESSMENTS',
  CONTENT_RESOURCES = 'CONTENT_RESOURCES',
  CLINICAL_RESOURCES = 'CLINICAL_RESOURCES',
}

export enum SyllabusItemAction {
  ADD_A_FOLDER = 'ADD_A_FOLDER',
  ADD_ADAPTIVE_LESSON = 'ADD_ADAPTIVE_LESSON',
  ADD_ADAPTIVE_QUIZ = 'ADD_ADAPTIVE_QUIZ',
  ADD_EBOOK_READING = 'ADD_EBOOK_READING',
  ADD_MORE_RESOURCES = 'ADD_MORE_RESOURCES',
  ADD_SHERPATH_LESSONS = 'ADD_SHERPATH_LESSONS',
  ADD_SHERPATH_SIMULATIONS = 'ADD_SHERPATH_SIMULATIONS',
  ADD_SHERPATH_SKILLS = 'ADD_SHERPATH_SKILLS',
  ADD_INTERACTIVE_REVIEW = 'ADD_INTERACTIVE_REVIEW',
  ADD_EVOLVE_INSTRUCTOR_RESOURCES = 'ADD_EVOLVE_INSTRUCTOR_RESOURCES',
  ADD_EVOLVE_STUDENT_RESOURCES = 'ADD_EVOLVE_STUDENT_RESOURCES',
  ADD_SIMULATION_INSTRUCTOR_RESOURCES = 'ADD_SIMULATION_INSTRUCTOR_RESOURCES',
  ADD_SIMULATION_STUDENT_RESOURCES = 'ADD_SIMULATION_STUDENT_RESOURCES',
  ADD_PERFORMANCE_CHECKLIST = 'ADD_PERFORMANCE_CHECKLIST',
  ADD_READING_ASSIGNMENT = 'ADD_READING_ASSIGNMENT',
  ADD_PATIENT_REPORT = 'ADD_PATIENT_REPORT',
  ADD_LAB_SIMULATION_IMPLEMENTATION_MODULE = 'ADD_LAB_SIMULATION_IMPLEMENTATION_MODULE',
  ADD_VR_SIMULATION_IMPLEMENTATION_MODULE = 'ADD_VR_SIMULATION_IMPLEMENTATION_MODULE',
  ADD_VR_SIMULATION = 'ADD_VR_SIMULATION',
  ADD_SIM_CHART_SIMULATION_EHR = 'ADD_SIM_CHART_SIMULATION_EHR',
  ADD_PRE_SIMULATION_QUIZ = 'ADD_PRE_SIMULATION_QUIZ',
  ADD_POST_SIMULATION_QUIZ = 'ADD_POST_SIMULATION_QUIZ',
  ADD_SHERPATH_GROUP_ACTIVITY = 'ADD_SHERPATH_GROUP_ACTIVITY',
  ADD_SIMCHART = 'ADD_SIMCHART',
  ADD_SHADOW_HEALTH = 'ADD_SHADOW_HEALTH',
  ADD_ASSESSMENT_BUILDER = 'ADD_ASSESSMENT_BUILDER',
  ADD_MCQ_QUIZ = 'ADD_MCQ_QUIZ',
  ADD_SHERPATH_POWERPOINT = 'ADD_SHERPATH_POWERPOINT',
  ADD_SHERPATH_CASE_STUDY = 'ADD_SHERPATH_CASE_STUDY',
  ADD_EXTERNAL_LINK = 'ADD_EXTERNAL_LINK',
  ADD_OSMOSIS_VIDEO = 'ADD_OSMOSIS_VIDEO',
  ADD_TOPIC_OVERVIEW = 'ADD_TOPIC_OVERVIEW',
  ADD_SCMO_NG = 'ADD_SCMO_NG',
  ADD_HESI_ASSESS = 'ADD_HESI_ASSESS',
  ADD_PRE_SIMULATION_EXERCISE = 'ADD_PRE_SIMULATION_EXERCISE',
  ADD_POST_SIMULATION_EXERCISE = 'ADD_POST_SIMULATION_EXERCISE',
  ADD_JOURNAL_EXERCISE = 'ADD_JOURNAL_EXERCISE',
  ADD_SBAR_EXERCISE = 'ADD_SBAR_EXERCISE',
  BULK_MOVE_REORDER = 'BULK_MOVE_REORDER',
  BULK_REMOVE = 'BULK_REMOVE',
  BULK_UNASSIGN = 'BULK_UNASSIGN',
  BULK_EDIT_SETTING = 'BULK_EDIT_SETTING',
  BULK_MAKE_VISIBLE_NOW = 'BULK_MAKE_VISIBLE_NOW',
  DEV_ADD_ASSIGNMENT = 'DEV_ADD_ASSIGNMENT',
  MOVE = 'MOVE',
  REMOVE = 'REMOVE',
  RENAME = 'RENAME',
  SETTINGS = 'SETTINGS',
  UNASSIGN = 'UNASSIGN',
  VIEW = 'VIEW',
  MAKE_VISIBLE_NOW = 'MAKE_VISIBLE_NOW',
  VIEW_PERFORMANCE = 'VIEW_PERFORMANCE',
  CREATE_DEEP_LINK = 'CREATE_DEEP_LINK',
  SELECT_ALL = 'SELECT_ALL',
  DESELECT_ALL = 'DESELECT_ALL'
}

export enum SyllabusItemConfigType {
  STATIC = 'STATIC', // Items that have a consistent contentId and can be found in the resource library
  CONFIGURABLE = 'CONFIGURABLE' // Items that are only addable and completely configurable by the user
}

export type SyllabusItemTypeConfig = {
  icon: string;
  displayName: string;
  displayNamePlural: string;
  iconPrefix: 'gizmo' | 'hmds';
  iconClass: string;
  title: string;
  sortOrder: number;
  recContentType: RecContentItemTypeDto;
  assignmentTypes: AssignmentType[];
  syllabusItemAction: SyllabusItemAction;
  configType: SyllabusItemConfigType;
  resourceCategory: ResourceCategory;
}

const caseStudy = 'case-study';

export const SyllabusItemTypeConfigMap: Record<SyllabusItemTypeDto, SyllabusItemTypeConfig> = {

  [ActiveSyllabusItemTypeDto.FOLDER]: {
    sortOrder: 2,
    recContentType: null,
    icon: 'open-folder',
    displayName: 'Folder',
    displayNamePlural: 'Folders',
    iconClass: null,
    iconPrefix: 'gizmo',
    title: 'Folder',
    assignmentTypes: null,
    syllabusItemAction: null,
    configType: null,
    resourceCategory: null
  },
  [ActiveSyllabusItemTypeDto.ADAPTIVE_QUIZ]: {
    sortOrder: 3,
    recContentType: null,
    icon: 'eugene',
    displayName: 'Adaptive Quiz (EAQ)',
    displayNamePlural: 'Adaptive Quizzes (EAQ)',
    iconClass: 'eaq',
    iconPrefix: 'hmds',
    title: 'Adaptive Quiz (EAQ)',
    assignmentTypes: [
      AssignmentType.MASTERY,
      AssignmentType.QUIZ_BY_QUESTION,
      AssignmentType.STANDARD,
      AssignmentType.AUTHESS,
    ],
    syllabusItemAction: SyllabusItemAction.ADD_ADAPTIVE_QUIZ,
    configType: SyllabusItemConfigType.CONFIGURABLE,
    resourceCategory: ResourceCategory.QUIZZES_ASSESSMENTS
  },
  [ActiveSyllabusItemTypeDto.ASSESSMENT_BUILDER_ASSIGNMENT]: {
    sortOrder: 4,
    recContentType: null,
    icon: 'empowering-knowledge',
    displayName: 'Assessment Builder Assignment',
    displayNamePlural: 'Assessment Builder Assignments',
    iconPrefix: 'gizmo',
    iconClass: 'eab',
    title: 'Assessment Builder Assignment',
    assignmentTypes: [AssignmentType.PERSONAL_ASSESSMENT_BUILDER],
    syllabusItemAction: SyllabusItemAction.ADD_ASSESSMENT_BUILDER,
    configType: SyllabusItemConfigType.CONFIGURABLE,
    resourceCategory: ResourceCategory.QUIZZES_ASSESSMENTS
  },
  // Front-end only entry (MCQ_QUIZ is never persisted): supplies the icon, title and sidebar action for MedEd MCQ, see getSyllabusItemDisplayType.
  [ActiveSyllabusItemTypeDto.MCQ_QUIZ]: {
    sortOrder: 40,
    recContentType: null,
    icon: 'help-outline-circle',
    displayName: 'Multiple choice Quiz (MCQ)',
    displayNamePlural: 'Multiple choice Quizzes (MCQ)',
    iconPrefix: 'gizmo',
    iconClass: 'mcq',
    title: 'Multiple choice Quiz (MCQ)',
    // MedEd MCQ assignments are QUIZ_BY_QUESTION and stay ADAPTIVE_QUIZ syllabus items; see getSyllabusItemDisplayType.
    assignmentTypes: [],
    syllabusItemAction: SyllabusItemAction.ADD_MCQ_QUIZ,
    configType: SyllabusItemConfigType.CONFIGURABLE,
    resourceCategory: ResourceCategory.QUIZZES_ASSESSMENTS
  },
  [ActiveSyllabusItemTypeDto.SHERPATH_CASE_STUDY]: {
    sortOrder: 5,
    recContentType: RecContentItemTypeDto.SHERPATH_CASE_STUDY,
    icon: caseStudy,
    displayName: 'Case Study',
    displayNamePlural: 'Case Studies',
    iconPrefix: 'hmds',
    iconClass: caseStudy,
    title: 'Case Study',
    assignmentTypes: [AssignmentType.SHERPATH_CASE_STUDY],
    syllabusItemAction: SyllabusItemAction.ADD_SHERPATH_CASE_STUDY,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.CUSTOM_LINK]: {
    sortOrder: 6,
    recContentType: null,
    icon: 'link',
    displayName: 'External Link',
    displayNamePlural: 'External Links',
    iconPrefix: 'gizmo',
    iconClass: 'custom-link',
    title: 'External Link',
    assignmentTypes: [AssignmentType.CUSTOM_LINK],
    syllabusItemAction: SyllabusItemAction.ADD_EXTERNAL_LINK,
    configType: SyllabusItemConfigType.CONFIGURABLE,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.EBOOK_READING]: {
    sortOrder: 7,
    recContentType: RecContentItemTypeDto.EBOOK_READING,
    icon: 'ebook-reading',
    displayName: 'eBook Reading',
    displayNamePlural: 'eBook Readings',
    iconPrefix: 'hmds',
    iconClass: 'ebook-reading',
    title: 'eBook Reading',
    assignmentTypes: [AssignmentType.EBOOK_READING],
    syllabusItemAction: SyllabusItemAction.ADD_EBOOK_READING,
    configType: SyllabusItemConfigType.CONFIGURABLE,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.EVOLVE_INSTRUCTOR_RESOURCE]: {
    sortOrder: 8,
    recContentType: RecContentItemTypeDto.EVOLVE_RESOURCE,
    icon: 'books',
    displayName: 'Evolve Instructor Resource',
    displayNamePlural: 'Evolve Instructor Resources',
    iconPrefix: 'gizmo',
    iconClass: 'evolve',
    title: 'Evolve Instructor Resource',
    assignmentTypes: [AssignmentType.EVOLVE_RESOURCE],
    syllabusItemAction: SyllabusItemAction.ADD_EVOLVE_INSTRUCTOR_RESOURCES,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.EVOLVE_STUDENT_RESOURCE]: {
    sortOrder: 9,
    recContentType: RecContentItemTypeDto.EVOLVE_RESOURCE,
    icon: 'books',
    displayName: 'Evolve Student Resource',
    displayNamePlural: 'Evolve Student Resources',
    iconPrefix: 'gizmo',
    iconClass: 'evolve',
    title: 'Evolve Student Resource',
    assignmentTypes: [AssignmentType.EVOLVE_RESOURCE],
    syllabusItemAction: SyllabusItemAction.ADD_EVOLVE_STUDENT_RESOURCES,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.SHERPATH_GROUP_ACTIVITY]: {
    sortOrder: 10,
    recContentType: RecContentItemTypeDto.SHERPATH_GROUP_ACTIVITY,
    icon: 'couple-of-people',
    displayName: 'Group Activity',
    displayNamePlural: 'Group Activities',
    iconPrefix: 'gizmo',
    iconClass: 'group-activity',
    title: 'Group Activity',
    assignmentTypes: [AssignmentType.SHERPATH_GROUP_ACTIVITY],
    syllabusItemAction: SyllabusItemAction.ADD_SHERPATH_GROUP_ACTIVITY,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.ADAPTIVE_LESSON]: {
    sortOrder: 11,
    recContentType: RecContentItemTypeDto.ADAPTIVE_LESSON,
    icon: 'lesson',
    displayName: 'Lesson',
    displayNamePlural: 'Lessons',
    iconPrefix: 'hmds',
    iconClass: 'lesson',
    title: 'Adaptive Lesson',
    assignmentTypes: [AssignmentType.ADAPTIVE_LESSON],
    syllabusItemAction: SyllabusItemAction.ADD_ADAPTIVE_LESSON,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.SHERPATH_LESSON]: {
    sortOrder: 12,
    recContentType: RecContentItemTypeDto.SHERPATH_LESSON,
    icon: 'lesson',
    displayName: 'Lesson',
    displayNamePlural: 'Lessons',
    iconPrefix: 'hmds',
    iconClass: 'lesson',
    title: 'Sherpath Lesson',
    assignmentTypes: [AssignmentType.LESSONS],
    syllabusItemAction: SyllabusItemAction.ADD_SHERPATH_LESSONS,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.OSMOSIS_VIDEO]: {
    sortOrder: 13,
    recContentType: RecContentItemTypeDto.OSMOSIS_VIDEO,
    icon: 'play-video',
    displayName: 'Osmosis Video',
    displayNamePlural: 'Osmosis Videos',
    iconPrefix: 'gizmo',
    iconClass: 'osmosis-video',
    title: 'Osmosis Video',
    assignmentTypes: [AssignmentType.OSMOSIS_VIDEO],
    syllabusItemAction: SyllabusItemAction.ADD_OSMOSIS_VIDEO,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.TOPIC_OVERVIEW]: {
    sortOrder: 13,
    recContentType: RecContentItemTypeDto.TOPIC_OVERVIEW,
    icon: 'books',
    displayName: 'Topic Overview',
    displayNamePlural: 'Topic Overviews',
    iconPrefix: 'gizmo',
    iconClass: 'topic-overview',
    title: 'Topic Overview',
    assignmentTypes: [],
    syllabusItemAction: SyllabusItemAction.ADD_TOPIC_OVERVIEW,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.PATIENT_REPORT]: {
    sortOrder: 14,
    recContentType: RecContentItemTypeDto.PATIENT_REPORT,
    icon: 'simulation-resource',
    displayName: 'Patient Report',
    displayNamePlural: 'Patient Report',
    iconPrefix: 'hmds',
    iconClass: 'simulation-resource',
    title: 'Patient Report',
    assignmentTypes: [AssignmentType.SLS_PATIENT_REPORT],
    syllabusItemAction: SyllabusItemAction.ADD_PATIENT_REPORT,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.SHERPATH_POWERPOINT]: {
    sortOrder: 15,
    recContentType: RecContentItemTypeDto.SHERPATH_POWERPOINT,
    icon: 'ppt',
    displayName: 'PowerPoint',
    displayNamePlural: 'PowerPoints',
    iconPrefix: 'gizmo',
    iconClass: 'ppt',
    title: 'PowerPoint',
    assignmentTypes: [AssignmentType.SHERPATH_POWERPOINT],
    syllabusItemAction: SyllabusItemAction.ADD_SHERPATH_POWERPOINT,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.READING_ASSIGNMENT]: {
    sortOrder: 16,
    recContentType: RecContentItemTypeDto.READING_ASSIGNMENT,
    icon: 'simulation-reading',
    displayName: 'Reading Assignment',
    displayNamePlural: 'Reading Assignment',
    iconPrefix: 'hmds',
    iconClass: 'simulation-reading',
    title: 'Reading Assignment',
    assignmentTypes: [AssignmentType.SLS_READING_ASSIGNMENT],
    syllabusItemAction: SyllabusItemAction.ADD_READING_ASSIGNMENT,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.LAB_SIMULATION_IMPLEMENTATION_MODULE]: {
    sortOrder: 17,
    recContentType: RecContentItemTypeDto.LAB_SIMULATION_IMPLEMENTATION_MODULE,
    icon: 'simulation-implementation',
    displayName: 'Lab Simulation Implementation Module',
    displayNamePlural: 'Lab Simulation Implementation Module',
    iconPrefix: 'hmds',
    iconClass: 'simulation-implementation',
    title: 'Lab Simulation Implementation Module',
    assignmentTypes: [],
    syllabusItemAction: SyllabusItemAction.ADD_LAB_SIMULATION_IMPLEMENTATION_MODULE,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.VR_SIMULATION_IMPLEMENTATION_MODULE]: {
    sortOrder: 18,
    recContentType: RecContentItemTypeDto.VR_SIMULATION_IMPLEMENTATION_MODULE,
    icon: 'simulation-implementation',
    displayName: 'Virtual Reality Simulation Implementation Module',
    displayNamePlural: 'Virtual Reality Simulation Implementation Module',
    iconPrefix: 'hmds',
    iconClass: 'simulation-implementation',
    title: 'Virtual Reality Simulation Implementation Module',
    assignmentTypes: [],
    syllabusItemAction: SyllabusItemAction.ADD_VR_SIMULATION_IMPLEMENTATION_MODULE,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.VR_SIMULATION]: {
    sortOrder: 19,
    recContentType: RecContentItemTypeDto.VR_SIMULATION,
    icon: 'stethoscope',
    displayName: 'Virtual Reality Simulation',
    displayNamePlural: 'Virtual Reality Simulation',
    iconPrefix: 'gizmo',
    iconClass: 'simulation-resource',
    title: 'Virtual Reality Simulation',
    assignmentTypes: [AssignmentType.SLS_VR_SIMULATION],
    syllabusItemAction: SyllabusItemAction.ADD_VR_SIMULATION,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.SLS_SIMCHART_SIMULATION_EHR]: {
    sortOrder: 20,
    recContentType: RecContentItemTypeDto.SLS_SIMCHART_SIMULATION_EHR,
    icon: 'stethoscope',
    displayName: 'Simulation EHR',
    displayNamePlural: 'Simulation EHR',
    iconPrefix: 'gizmo',
    iconClass: 'simulation-resource',
    title: 'Simulation EHR',
    assignmentTypes: [AssignmentType.SLS_SIMCHART_SIMULATION_EHR],
    syllabusItemAction: SyllabusItemAction.ADD_SIM_CHART_SIMULATION_EHR,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.SHADOW_HEALTH_ASSIGNMENT]: {
    sortOrder: 21,
    recContentType: RecContentItemTypeDto.SHADOW_HEALTH,
    icon: 'lesson',
    displayName: 'Shadow Health Assignment',
    displayNamePlural: 'Shadow Health Assignments',
    iconPrefix: 'hmds',
    iconClass: 'shadow-health',
    title: 'Shadow Health Assignment',
    assignmentTypes: [AssignmentType.SHADOW_HEALTH],
    syllabusItemAction: SyllabusItemAction.ADD_SHADOW_HEALTH,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.SIMCHART_CASE_STUDY]: {
    sortOrder: 22,
    recContentType: RecContentItemTypeDto.SIM_CHART,
    icon: caseStudy,
    displayName: 'SimChart Case Study',
    displayNamePlural: 'SimChart Case Studies',
    iconPrefix: 'hmds',
    iconClass: caseStudy,
    title: 'SimChart Case Study',
    assignmentTypes: [AssignmentType.CASE_STUDY],
    syllabusItemAction: SyllabusItemAction.ADD_SIMCHART,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.SIMCHART_CHART]: {
    sortOrder: 23,
    recContentType: null,
    icon: 'simulation-nursing',
    displayName: 'SimChart Empty EHR',
    displayNamePlural: 'SimChart Empty EHRs',
    iconPrefix: 'hmds',
    iconClass: 'simchart',
    title: 'SimChart Empty EHR',
    assignmentTypes: [AssignmentType.CHART],
    syllabusItemAction: SyllabusItemAction.ADD_SIMCHART,
    configType: SyllabusItemConfigType.CONFIGURABLE,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.SHERPATH_SIMULATIONS]: {
    sortOrder: 24,
    recContentType: RecContentItemTypeDto.SHERPATH_SIMULATION,
    icon: 'stethoscope',
    displayName: 'Simulation',
    displayNamePlural: 'Simulations',
    iconPrefix: 'gizmo',
    iconClass: 'simulation',
    title: 'Simulation',
    assignmentTypes: [AssignmentType.SIMULATIONS],
    syllabusItemAction: SyllabusItemAction.ADD_SHERPATH_SIMULATIONS,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.INTERACTIVE_REVIEW]: {
    sortOrder: 25,
    recContentType: RecContentItemTypeDto.REVIEW_QUIZ,
    icon: 'insert-question',
    displayName: 'Interactive Review Questions',
    displayNamePlural: 'Interactive Review Questions',
    iconPrefix: 'hmds',
    iconClass: 'interactive-review',
    title: 'Interactive Review Questions',
    assignmentTypes: [AssignmentType.INTERACTIVE_REVIEW],
    syllabusItemAction: SyllabusItemAction.ADD_INTERACTIVE_REVIEW,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.QUIZZES_ASSESSMENTS
  },
  [ActiveSyllabusItemTypeDto.SIMULATION_INSTRUCTOR_RESOURCE]: {
    sortOrder: 26,
    recContentType: null,
    icon: 'simulation-resource',
    displayName: 'Simulation Instructor Resource',
    displayNamePlural: 'Simulation Instructor Resource',
    iconPrefix: 'hmds',
    iconClass: 'simulation-resource',
    title: 'Simulation Instructor Resource',
    assignmentTypes: [],
    syllabusItemAction: SyllabusItemAction.ADD_SIMULATION_INSTRUCTOR_RESOURCES,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.SIMULATION_STUDENT_RESOURCE]: {
    sortOrder: 27,
    recContentType: null,
    icon: 'simulation-resource',
    displayName: 'Simulation Student Resource',
    displayNamePlural: 'Simulation Student Resource',
    iconPrefix: 'hmds',
    iconClass: 'simulation-resource',
    title: 'Simulation Student Resource',
    assignmentTypes: [],
    syllabusItemAction: SyllabusItemAction.ADD_SIMULATION_STUDENT_RESOURCES,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.SHERPATH_SKILL]: {
    sortOrder: 28,
    recContentType: RecContentItemTypeDto.SHERPATH_SKILL,
    icon: 'hand',
    displayName: 'Skill',
    displayNamePlural: 'Skills',
    iconPrefix: 'hmds',
    iconClass: 'skill',
    title: 'Skill',
    assignmentTypes: [AssignmentType.SKILL],
    syllabusItemAction: SyllabusItemAction.ADD_SHERPATH_SKILLS,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.PRE_SIMULATION_QUIZ]: {
    sortOrder: 29,
    recContentType: RecContentItemTypeDto.PRE_SIMULATION_QUIZ,
    icon: 'simulation-quiz',
    displayName: 'Pre-Simulation Quiz',
    displayNamePlural: 'Pre-Simulation Quiz',
    iconPrefix: 'hmds',
    iconClass: 'simulation-quiz',
    title: 'Pre-Simulation Quiz',
    assignmentTypes: [AssignmentType.SLS_PRE_SIMULATION_QUIZ],
    syllabusItemAction: SyllabusItemAction.ADD_PRE_SIMULATION_QUIZ,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.QUIZZES_ASSESSMENTS
  },
  [ActiveSyllabusItemTypeDto.POST_SIMULATION_QUIZ]: {
    sortOrder: 30,
    recContentType: RecContentItemTypeDto.POST_SIMULATION_QUIZ,
    icon: 'simulation-quiz',
    displayName: 'Post-Simulation Quiz',
    displayNamePlural: 'Post-Simulation Quiz',
    iconPrefix: 'hmds',
    iconClass: 'simulation-quiz',
    title: 'Post-Simulation Quiz',
    assignmentTypes: [AssignmentType.SLS_POST_SIMULATION_QUIZ],
    syllabusItemAction: SyllabusItemAction.ADD_POST_SIMULATION_QUIZ,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.QUIZZES_ASSESSMENTS
  },
  [ActiveSyllabusItemTypeDto.SCMO_NG]: {
    sortOrder: 31,
    recContentType: null,
    icon: 'stethoscope',
    displayName: 'SimChart for the Medical Office Assignment',
    displayNamePlural: 'SimChart for the Medical Office Assignments',
    iconPrefix: 'gizmo',
    iconClass: 'simulation-resource',
    title: 'SimChart for the Medical Office Assignment',
    assignmentTypes: [AssignmentType.SCMO_NG],
    syllabusItemAction: SyllabusItemAction.ADD_SCMO_NG,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.HESI_ASSESS]: {
    sortOrder: 32,
    recContentType: RecContentItemTypeDto.HESI_ASSESS,
    icon: 'insert-question',
    displayName: 'HESI Assess',
    displayNamePlural: 'HESI Assessments',
    iconPrefix: 'hmds',
    iconClass: 'hesi-assess',
    title: 'HESI Assess',
    assignmentTypes: [AssignmentType.HESI_ASSESS],
    syllabusItemAction: SyllabusItemAction.ADD_HESI_ASSESS,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.QUIZZES_ASSESSMENTS
  },
  [DeprecatedSyllabusItemTypeDto.EVOLVE_RESOURCE]: {
    sortOrder: 100,
    recContentType: null,
    icon: 'books',
    displayName: 'Evolve Resource',
    displayNamePlural: 'Evolve Resources',
    iconPrefix: 'gizmo',
    iconClass: 'evolve',
    title: 'Evolve Resource',
    assignmentTypes: null,
    syllabusItemAction: null,
    configType: null,
    resourceCategory: null
  },
  [DeprecatedSyllabusItemTypeDto.SIMCHART]: {
    sortOrder: 100,
    recContentType: null,
    icon: 'simulation-nursing',
    displayName: 'SimChart',
    displayNamePlural: 'SimCharts',
    iconPrefix: 'hmds',
    iconClass: 'simchart',
    title: 'SimChart',
    assignmentTypes: null,
    syllabusItemAction: null,
    configType: null,
    resourceCategory: null
  },
  [ActiveSyllabusItemTypeDto.PRE_SIMULATION_EXERCISE]: {
    sortOrder: 33,
    recContentType: RecContentItemTypeDto.PRE_SIMULATION_EXERCISE,
    icon: 'simulation-exercise',
    displayName: 'Simulation Exercise',
    displayNamePlural: 'Simulation Exercises',
    iconPrefix: 'hmds',
    iconClass: 'simulation-exercise',
    title: 'Simulation Exercise',
    assignmentTypes: [AssignmentType.SLS_JOURNALING_EXERCISE],
    syllabusItemAction: SyllabusItemAction.ADD_PRE_SIMULATION_EXERCISE,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.QUIZZES_ASSESSMENTS
  },
  [ActiveSyllabusItemTypeDto.POST_SIMULATION_EXERCISE]: {
    sortOrder: 34,
    recContentType: RecContentItemTypeDto.POST_SIMULATION_EXERCISE,
    icon: 'simulation-exercise',
    displayName: 'Simulation Exercise',
    displayNamePlural: 'Simulation Exercises',
    iconPrefix: 'hmds',
    iconClass: 'simulation-exercise',
    title: 'Simulation Exercise',
    assignmentTypes: [AssignmentType.SLS_JOURNALING_EXERCISE],
    syllabusItemAction: SyllabusItemAction.ADD_POST_SIMULATION_EXERCISE,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.QUIZZES_ASSESSMENTS
  },
  [ActiveSyllabusItemTypeDto.JOURNAL_EXERCISE]: {
    sortOrder: 35,
    recContentType: RecContentItemTypeDto.JOURNAL_EXERCISE,
    icon: 'simulation-exercise',
    displayName: 'Simulation Exercise',
    displayNamePlural: 'Simulation Exercises',
    iconPrefix: 'hmds',
    iconClass: 'simulation-exercise',
    title: 'Simulation Exercise',
    assignmentTypes: [AssignmentType.SLS_JOURNALING_EXERCISE],
    syllabusItemAction: SyllabusItemAction.ADD_JOURNAL_EXERCISE,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.QUIZZES_ASSESSMENTS
  },
  [ActiveSyllabusItemTypeDto.SBAR_EXERCISE]: {
    sortOrder: 36,
    recContentType: RecContentItemTypeDto.SBAR_EXERCISE,
    icon: 'simulation-exercise',
    displayName: 'Simulation Exercise',
    displayNamePlural: 'Simulation Exercises',
    iconPrefix: 'hmds',
    iconClass: 'simulation-exercise',
    title: 'Simulation Exercise',
    assignmentTypes: [AssignmentType.SLS_JOURNALING_EXERCISE],
    syllabusItemAction: SyllabusItemAction.ADD_SBAR_EXERCISE,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.QUIZZES_ASSESSMENTS
  },
  [ActiveSyllabusItemTypeDto.VERBAL_REPORT_TEMPLATE]: {
    sortOrder: 37,
    recContentType: RecContentItemTypeDto.VERBAL_REPORT_TEMPLATE,
    icon: 'simulation-resource',
    displayName: 'Verbal Report Template',
    displayNamePlural: 'Verbal Report Templates',
    iconPrefix: 'hmds',
    iconClass: 'simulation-resource',
    title: 'Verbal Report Template',
    assignmentTypes: [AssignmentType.SLS_VERBAL_REPORT_TEMPLATE],
    syllabusItemAction: SyllabusItemAction.ADD_SIMULATION_STUDENT_RESOURCES,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.OBSERVER_EVALUATION_RUBRIC]: {
    sortOrder: 38,
    recContentType: RecContentItemTypeDto.OBSERVER_EVALUATION_RUBRIC,
    icon: 'simulation-resource',
    displayName: 'Observer Evaluation Rubric',
    displayNamePlural: 'Observer Evaluation Rubrics',
    iconPrefix: 'hmds',
    iconClass: 'simulation-resource',
    title: 'Observer Evaluation Rubric',
    assignmentTypes: [AssignmentType.SLS_OBSERVER_EVALUATION_RUBRIC],
    syllabusItemAction: SyllabusItemAction.ADD_SIMULATION_STUDENT_RESOURCES,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
  [ActiveSyllabusItemTypeDto.PERFORMANCE_CHECKLIST]: {
    sortOrder: 39,
    recContentType: RecContentItemTypeDto.PERFORMANCE_CHECKLIST,
    icon: 'simulation-resource',
    displayName: 'Performance Checklist',
    displayNamePlural: 'Performance Checklists',
    iconPrefix: 'hmds',
    iconClass: 'simulation-resource',
    title: 'Performance Checklist',
    assignmentTypes: [AssignmentType.SLS_PERFORMANCE_CHECKLIST],
    syllabusItemAction: SyllabusItemAction.ADD_PERFORMANCE_CHECKLIST,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CLINICAL_RESOURCES
  },
};

export enum UnSupportedEvolveResourceTypeDEPRECATED {
  LMS = 'LMS',
  DOWNLOAD_BY_RESOURCE_TYPE = 'Download by Resource Type',
  DOWNLOADS = 'downloads'
}

export enum DefaultCoursePlanEvolveResourceTypeDEPRECATED {
  CASE_STUDIES = 'Case studies',
  CASE_STUDY = 'Case study',
  TEACH = 'TEACH',
  PPT = 'PPT',
}
